import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../../components/page-header';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/toast';
import {
  SeatInfo,
  getSeatInfo,
  cancelSubscription,
  switchBillingTerm,
} from '../../../../application/routes/settings/components/PaymentMethods/paymentApi';
import { DEV_MOCK, MOCK_SEAT_INFO } from './mock';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

export function CancellationGatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [switchingTerm, setSwitchingTerm] = useState(false);

  const fetchSeatInfo = useCallback(async () => {
    if (DEV_MOCK) {
      setSeatInfo(MOCK_SEAT_INFO as unknown as SeatInfo);
      setLoading(false);
      return;
    }
    if (!orgId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await getSeatInfo(orgId);
      if (res.success && res.data) {
        setSeatInfo(res.data);
      }
    } catch {
      // If we can't load seat info, still show the page with defaults
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchSeatInfo();
  }, [fetchSeatInfo]);

  const handleSwitchToAnnual = async () => {
    if (DEV_MOCK) {
      toast.success('Switched to annual billing');
      navigate('/v2/settings/billing');
      return;
    }
    if (!orgId) {
      return;
    }
    try {
      setSwitchingTerm(true);
      const res = await switchBillingTerm(orgId, 'yearly');
      if (res.success) {
        toast.success('Switched to annual billing');
        navigate('/v2/settings/billing');
      } else {
        toast.error(res.error || 'Failed to switch billing term');
      }
    } catch {
      toast.error('Unable to switch billing term. Please try again.');
    } finally {
      setSwitchingTerm(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (DEV_MOCK) {
      navigate('/v2/settings/billing/cancel/confirmed');
      return;
    }
    if (!orgId) {
      return;
    }
    try {
      setCanceling(true);
      const res = await cancelSubscription(orgId, false);
      if (res.success) {
        navigate('/v2/settings/billing/cancel/confirmed');
      } else {
        toast.error(res.error || 'Failed to cancel subscription');
      }
    } catch {
      toast.error('Unable to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Cancel Plan"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const isQuarterly =
    seatInfo?.billingTerm?.toLowerCase() !== 'yearly' &&
    seatInfo?.billingTerm?.toLowerCase() !== 'annual';

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Cancel Plan"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Back link */}
          <button
            onClick={() => navigate('/v2/settings/billing')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to Billing
          </button>

          {/* Warning icon and headline */}
          <div className="text-center space-y-4">
            <div className="mx-auto size-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="size-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              Before you go — are you sure?
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Cancelling will end your access to Paiv at the end of your current
              billing period. Your data will be saved for 90 days.
            </p>
          </div>

          {/* Save offer card */}
          <div className="border border-border rounded-xl p-6 bg-card space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              Switch to annual and save
            </h3>
            <p className="text-sm text-muted-foreground">
              Switch to annual and lock in $50/seat/mo — that&apos;s $300/seat
              less per year, plus 10 free estimates and 10 measurement reports.
            </p>
            <Button
              onClick={handleSwitchToAnnual}
              disabled={switchingTerm || canceling}
              className="w-full sm:w-auto"
            >
              {switchingTerm ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              Switch to annual instead
            </Button>
          </div>

          {/* Confirm cancel */}
          <div className="border-t border-border pt-6">
            <Button
              variant="outline"
              className="text-destructive border-destructive/50 hover:bg-destructive/10"
              onClick={handleConfirmCancel}
              disabled={canceling || switchingTerm}
            >
              {canceling ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              Cancel my plan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
